import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { useLang } from "../lang";

const EASE = [0.16, 1, 0.3, 1];

function MaskedLine({ children, delay, accent }) {
  return (
    <span className="block overflow-hidden">
      <motion.span
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.1, ease: EASE, delay }}
        className={`block font-display font-semibold uppercase leading-[0.92] tracking-tight ${
          accent ? "text-signal" : "text-foreground"
        }`}
        style={{ wordSpacing: "0.18em" }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero({ onBook }) {
  const { t } = useLang();
  const imgWrap = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgWrap, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-60, 60]);

  return (
    <section id="top" className="relative min-h-screen border-b border-edge/60 px-6 pt-32 md:px-10 md:pt-40">
      <div className="grid grid-cols-1 gap-16 md:grid-cols-12">
        <div className="md:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-8 font-mono text-[11px] uppercase tracking-[0.25em] text-signal"
            data-testid="hero-overline"
          >
            {t.hero.overline}
          </motion.p>

          <h1 className="text-[15vw] md:text-[7.5vw]" data-testid="hero-headline">
            {t.hero.lines.map((line, i) => (
              <MaskedLine key={line} delay={0.35 + i * 0.14} accent={i === 2}>
                {line}
              </MaskedLine>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 1.0 }}
            className="mt-10 max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg"
            data-testid="hero-sub"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 1.15 }}
            className="mt-12 flex flex-wrap items-center gap-6"
          >
            <button
              onClick={onBook}
              data-testid="hero-booking-btn"
              className="group flex items-center gap-3 rounded-full bg-signal px-8 py-4 font-mono text-xs uppercase tracking-[0.15em] text-white transition-colors duration-200 hover:bg-signalhover"
            >
              {t.hero.cta1}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </button>
            <a
              href="#manifeste"
              data-testid="hero-manifesto-link"
              className="group flex items-center gap-3 border-b border-edge pb-1 font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground transition-colors duration-200 hover:border-signal hover:text-foreground"
            >
              {t.hero.cta2}
              <ArrowDown className="h-4 w-4 transition-transform duration-200 group-hover:translate-y-1" />
            </a>
          </motion.div>

          <motion.ul
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.4 }}
            className="mt-16 flex flex-wrap gap-x-8 gap-y-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground"
            data-testid="hero-ticks"
          >
            {t.hero.ticks.map((tick) => (
              <li key={tick} className="flex items-center gap-2">
                <span className="h-1 w-1 bg-signal" />
                {tick}
              </li>
            ))}
          </motion.ul>
        </div>

        <div className="hidden md:col-span-5 md:block" ref={imgWrap}>
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.2, ease: EASE, delay: 0.6 }}
            className="relative ml-auto mt-10 aspect-[3/4] w-full max-w-md overflow-hidden border border-edge"
            data-testid="hero-image-frame"
          >
            <motion.img
              src="https://images.unsplash.com/photo-1565626424178-c699f6601afd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxldXJvcGVhbiUyMGFyY2hpdGVjdHVyZSUyMGJydXRhbGlzdCUyMGNvbmNyZXRlfGVufDB8fHx8MTc4OTM2ODUwOXww&ixlib=rb-4.1.0&q=85"
              alt="Architecture brutaliste européenne — TERRAIN, studio de déploiement IA"
              style={{ y, scale: 1.15 }}
              className="h-full w-full object-cover grayscale"
              data-testid="hero-image"
            />
            <div className="absolute inset-0 bg-void/30" />
            <div className="absolute bottom-0 left-0 flex w-full items-center justify-between border-t border-edge/60 bg-void/80 px-4 py-3 backdrop-blur-sm">
              <span className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground">{t.hero.caption}</span>
              <span className="h-2 w-2 animate-pulse rounded-full bg-signal" />
            </div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-6 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground md:left-10"
      >
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}>
          <ArrowDown className="h-3.5 w-3.5" />
        </motion.span>
        {t.hero.scroll}
      </motion.div>
    </section>
  );
}
